import React, {
    useEffect,
    useState,
} from "react";

import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import {
    FiFilter,
    FiRefreshCcw,
    FiSearch,
    FiX,
} from "react-icons/fi";

import ReassignTaskModal from "../components/table/ReassignTaskModal";
import { API_URL } from "../creds";
import api from "../api/axios";
import TableViewAdminPage from "../components/admin/TableViewAdminPage";
import languages from "../utils/languages.json";

const TaskPage = () => {
    // STATES
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [openFilter, setOpenFilter] = useState(false);
    const [openReassign, setOpenReassign] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [suggestions, setSuggestions] = useState([]);

    const [filters, setFilters] =
        useState({
            source_lang: null,
            target_lang: null,
            status: null,
        });

    // TOKEN
    const token = localStorage.getItem("access_token");


    const statusOptions = [
        {
            value: "Available",
            label: "Available",
        },
        {
            value: "Under Progress",
            label: "Under Progress",
        },
        {
            value: "Completed",
            label: "Completed",
        },
    ];

    // FETCH USERS
    const fetchUsers = async () => {

        try {

            const res =
                await api.get("/users");

            const translators =
                Array.isArray(res.data)
                    ? res.data.map((u) => ({
                        value: u.email,
                        label: `${u.name} (${u.email})`,
                    }))
                    : [];

            setUsers(translators);

        } catch (err) {

            console.log(err);

            toast.error(
                "Failed to fetch users"
            );
        }
    };


    // FETCH TASKS
    const fetchTasks = async () => {

        try {

            setLoading(true);

            const params = {};

            if (filters.source_lang) {
                params.source_lang = filters.source_lang.value;
            }

            if (filters.target_lang) {
                params.target_lang = filters.target_lang.value;
            }

            if (filters.status) {
                params.status = filters.status.value;
            }

            if (search) {
                params.search = search;
            }

            const res = await api.get("/filter-tasks", { params });
            setTasks(Array.isArray(res.data) ? res.data : res.data.tasks || []);
        } catch (err) {
            console.log(err);
            setTasks([]);
            toast.error("Failed to fetch tasks");
        } finally {
            setLoading(false);
        }
    };


    // =====================================================
    // REASSIGN TASK
    // =====================================================
    const reassignTask = async (
        taskId,
        translatorId
    ) => {

        try {

            await api.put(`/tasks/${taskId}/reassign`, { translator_id: translatorId, });
            toast.success("Task reassigned successfully");
            fetchTasks();
        } catch (err) {
            console.log(err);
            toast.error(
                err?.response?.data
                    ?.detail ||
                "Failed to reassign task"
            );

            throw err;
        }
    };

    // =====================================================
    // INITIAL LOAD
    // =====================================================
    useEffect(() => {

        fetchUsers();
        fetchTasks();

    }, []);

    const fetchSuggestions = async (value) => {

        if (!value || value.length < 2) {
            setSuggestions([]);
            return;
        }

        try {

            const res = await api.get(
                "/tasks/search/suggestions",
                {
                    params: {
                        q: value
                    }
                }
            );

            setSuggestions(res.data || []);

        } catch (err) {

            console.log(err);

            setSuggestions([]);
        }
    };

    const languageOptions = [...languages]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((lang) => ({
            value: lang.code,
            label: `${lang.name} (${lang.code})`,
        }));


    return (
        <main className="min-h-screen bg-gray-100 p-6">
            {/* HEADER */}
            <div
                className="
                    flex
                    flex-col
                    md:flex-row
                    md:items-center
                    md:justify-between
                    gap-4
                    mb-8
                "
            >
                {/* LEFT */}
                <div className="flex items-center gap-4 w-full">
                    {/* SEARCH */}
                    <div className="relative w-full max-w-xl">

                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                            type="text"
                            placeholder="Search taskid, file, language, translator..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                fetchSuggestions(e.target.value);
                            }}
                            className=" 
                w-full
                pl-12
                pr-4
                py-3
                rounded-2xl
                border
                border-gray-200
                focus:outline-none
                focus:ring-2
                focus:ring-indigo-500
                shadow-sm
                bg-white
            "
                        />

                        {/* SUGGESTIONS */}
                        {suggestions.length > 0 && (
                            <div
                                className="
                    absolute
                    z-50
                    mt-2
                    w-full
                    bg-white
                    border
                    border-gray-200
                    rounded-2xl
                    shadow-xl
                    overflow-hidden
                "
                            >

                                {suggestions.map((item, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setSearch(item);
                                            setSuggestions([]);
                                            fetchTasks();
                                        }}
                                        className="w-full text-left px-4 py-3 hover:bg-indigo-50 border-b last:border-b-0"
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>


                {/* RIGHT */}
                <div className="flex gap-3">
                    {/* FILTER */}
                    <button
                        onClick={() => setOpenFilter(true)}
                        className="flex items-center gap-2 bg-white border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 text-gray-700 px-5 py-3 rounded-xl transition shadow-sm"
                    >
                        <FiFilter />
                        Filters
                    </button>


                    {/* REFRESH */}
                    <button
                        onClick={fetchTasks}
                        className="
                            flex
                            items-center
                            gap-2
                            bg-indigo-600
                            hover:bg-indigo-700
                            text-white
                            px-5
                            py-3
                            rounded-xl
                            shadow-md
                            transition
                        "
                    >
                        <FiRefreshCcw />
                        Refresh
                    </button>
                </div>
            </div>


            {/* ================================================= */}
            {/* FILTER MODAL */}
            {/* ================================================= */}
            {openFilter && (
                <div
                    className="
                        fixed
                        inset-0
                        bg-black/40
                        z-50
                        flex
                        items-center
                        justify-center
                        p-4
                    "
                >

                    <div
                        className="
                            bg-white
                            rounded-3xl
                            shadow-2xl
                            w-full
                            max-w-2xl
                            p-8
                            relative
                        "
                    >

                        {/* CLOSE */}
                        <button
                            onClick={() =>
                                setOpenFilter(
                                    false
                                )
                            }
                            className="
                                absolute
                                top-5
                                right-5
                                p-2
                                rounded-lg
                                hover:bg-gray-100
                            "
                        >
                            <FiX />
                        </button>


                        {/* TITLE */}
                        <h2
                            className="
                                text-2xl
                                font-bold
                                text-gray-800
                                mb-6
                            "
                        >
                            Filter Tasks
                        </h2>


                        {/* FILTER GRID */}
                        <div
                            className="
                                grid
                                grid-cols-1
                                md:grid-cols-2
                                gap-5
                            "
                        >

                            {/* LANGUAGE */}
                            <div>
                                <label className="text-sm text-gray-600 mb-2 block">
                                    Source Language
                                </label>

                                <Select
                                    options={languageOptions}
                                    isClearable
                                    value={filters.source_lang}
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            source_lang: e,
                                        })
                                    }
                                />
                            </div>

                            <div>
                                <label className="text-sm text-gray-600 mb-2 block">
                                    Target Language
                                </label>

                                <Select
                                    options={languageOptions}
                                    isClearable
                                    value={filters.target_lang}
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            target_lang: e,
                                        })
                                    }
                                />
                            </div>


                            {/* STATUS */}
                            <div>
                                <label className="text-sm text-gray-600 mb-2 block">
                                    Status
                                </label>

                                <Select
                                    options={
                                        statusOptions
                                    }
                                    isClearable
                                    value={
                                        filters.status
                                    }
                                    onChange={(e) =>
                                        setFilters({
                                            ...filters,
                                            status: e,
                                        })
                                    }
                                />
                            </div>
                        </div>


                        {/* ACTIONS */}
                        <div
                            className="
                                flex
                                justify-end
                                gap-4
                                mt-8
                            "
                        >

                            {/* RESET */}
                            <button
                                onClick={() => {

                                    setFilters({
                                        source_lang: null,
                                        target_lang: null,
                                        status: null,
                                    });

                                    toast.success(
                                        "Filters reset"
                                    );
                                }}
                                className="
                                    px-5
                                    py-3
                                    rounded-xl
                                    border
                                    border-gray-200
                                    hover:bg-gray-100
                                "
                            >
                                Reset
                            </button>


                            {/* APPLY */}
                            <button
                                onClick={() => {

                                    fetchTasks();

                                    setOpenFilter(
                                        false
                                    );

                                    toast.success(
                                        "Filters applied"
                                    );
                                }}
                                className="
                                    flex
                                    items-center
                                    gap-2
                                    bg-indigo-600
                                    hover:bg-indigo-700
                                    text-white
                                    px-6
                                    py-3
                                    rounded-xl
                                    shadow-md
                                "
                            >
                                <FiSearch />
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TABLE */}
            <div className="bg-white rounded-3xl border border-gray-200 shadow-sm">
                <TableViewAdminPage loading={loading} tasks={tasks} setSelectedTask={setSelectedTask} setOpenReassign={setOpenReassign} />
            </div>

            {/* REASSIGN MODAL */}
            <ReassignTaskModal
                open={openReassign}
                onClose={() => setOpenReassign(false)}
                users={users}
                task={selectedTask}
                onSubmit={reassignTask}
            />
        </main>
    );
};

export default TaskPage;